import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { trackSegment } from '~/components/SegmentAnalytics';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { UserAttributes } from '~/redux/api/methods/roles/roles';
import { ParsedType } from '~/redux/api/transformers/utils';
import { coerceRtkqError } from '~/redux/api/utils';
import { Nullable } from '~/types/Nullable';

type Props = {
  user: Nullable<ParsedType<UserAttributes>>;
  visible: boolean;
  onHide: () => void;
  isWorkdayUser?: boolean;
};

export const DeactivateUserModal = ({
  user,
  visible,
  onHide,
  isWorkdayUser = false,
}: Props) => {
  const intl = useIntl();
  const [deactivateUser, deactivateUserResult] =
    api.endpoints.deactivateUser.useMutation();
  const { toast } = useToast();
  const { isError, isLoading, isSuccess, error } = deactivateUserResult;
  const currentUser = useCurrentUser();

  const isRolodexDataSourceEnabled = useFlag(FlagType.RolodexAsUserDataSource);

  useEffect(() => {
    if (isSuccess) {
      toast({
        message: intl.formatMessage(
          {
            id: 'DeactivateUserModal.successMessage',
            defaultMessage: '"{firstName} {lastName}" deactivated.',
            description:
              'Success message when user is deactivated. Example: firstName = "John", lastName = "Smith"',
          },
          { firstName: user?.firstName, lastName: user?.lastName },
        ),
        status: 'success',
      });
      onHide();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      const resp = coerceRtkqError(error)?.response || {};
      const msg = resp.data?.errors?.[0];
      toast({
        message:
          msg?.title ||
          intl.formatMessage({
            id: 'DeactivateUserModal.errorMessage',
            defaultMessage: 'An error occurred while deleting the user.',
            description: 'Error message when user deactivation fails',
          }),
        status: 'danger',
      });
    }
  }, [isError]);

  return (
    <Modal
      isVisible={visible}
      title={intl.formatMessage({
        id: 'DeactivateUserModal.title',
        defaultMessage: 'Deactivate User?',
        description: 'Modal title for user deactivation',
      })}
      onCancel={onHide}
      onHide={onHide}
      primaryAction={{
        text: intl.formatMessage({
          id: 'DeactivateUserModal.deactivateButton',
          defaultMessage: 'Deactivate',
          description: 'Button text for user deactivation',
        }),
        onClick: () => {
          trackSegment('submitDeleteUser', {
            id: user!.id,
            user_id: currentUser.id,
            client_id: currentUser.client,
          });
          deactivateUser({
            userId: user!.id,
            enableRolodex: isWorkdayUser || isRolodexDataSourceEnabled,
          });
        },
        isLoading: isLoading,
        variant: 'danger',
      }}
    >
      <Text>
        <FormattedMessage
          id="DeactivateUserModal.confirmationText"
          defaultMessage='Are you sure you want to deactivate "{firstName} {lastName}"? Users can be reactivated at any time on the Users page.'
          description='Confirmation message for deactivating a user. Example: firstName = "John", lastName = "Smith"'
          values={{
            firstName: user?.firstName,
            lastName: user?.lastName,
          }}
        />
      </Text>
    </Modal>
  );
};
